/**
 * ViewportObserver utility class.
 * Allows Lit components to be aware of the viewport size and respond to changes.
 */
class ViewportObserver {
  constructor() {
    this.listeners = []
    this.mediaQueries = {
      mobile: window.matchMedia('(max-width: 767px)'),
      tablet: window.matchMedia('(min-width: 768px) and (max-width: 1023px)'),
      desktop: window.matchMedia('(min-width: 1024px) and (max-width: 1440px)'),
      widescreen: window.matchMedia('(min-width: 1440px)'),
    }

    this.currentViewport = this.getCurrentViewport()

    // Attach event listeners for media query changes
    for (const mediaQuery of Object.values(this.mediaQueries)) {
      mediaQuery.addEventListener('change', this._handleMediaQueryChange)
    }
  }

  /**
   * Determines the current viewport based on the defined media queries.
   * @returns {string} The current viewport size
   */
  getCurrentViewport() {
    if (this.mediaQueries.mobile.matches) {
      return 'mobile'
    }
    if (this.mediaQueries.tablet.matches) {
      return 'tablet'
    }
    if (this.mediaQueries.desktop.matches) {
      return 'desktop'
    }
    return 'widescreen'
  }

  /**
   * Event handler for media query changes.
   * Updates the current viewport and notifies the registered listeners.
   */
  _handleMediaQueryChange = () => {
    const newViewport = this.getCurrentViewport()
    if (newViewport !== this.currentViewport) {
      this.currentViewport = newViewport
      this.notifyListeners()
    }
  }

  /**
   * Registers a listener to be notified of viewport changes.
   * @param {function} listener - The listener function to be called when the viewport changes.
   */
  addListener(listener) {
    this.listeners.push(listener)
  }

  /**
   * Removes a previously registered listener.
   * @param {function} listener - The listener function to be removed.
   */
  removeListener(listener) {
    this.listeners = this.listeners.filter(
      (_listener) => _listener !== listener,
    )
  }

  /**
   * Notifies all registered listeners of the current viewport.
   */
  notifyListeners() {
    for (const listener of this.listeners) {
      listener(this.currentViewport)
    }
  }
}

// Create a singleton instance of ViewportObserver
export const viewportObserver = new ViewportObserver()

/**
 * Checks whether a value represents a valid number.
 * @example
 * validateNumber(NaN)       // returns false
 * validateNumber(undefined) // returns false
 * validateNumber(1)         // returns true
 * validateNumber(1.1)       // returns true
 * validateNumber('1')       // returns true
 * validateNumber('1.1')     // returns true
 * validateNumber('$1.00')   // returns false (preformatted price needs a different check)
 *
 * @param {*} value - The value to check.
 * @returns {boolean} - True if value is a valid number, false otherwise.
 */
const validateNumber = (value) => Number.isFinite(Number.parseFloat(value))

/**
 * Converts number representing a price in minor unit (e.g. cents) to major unit (e.g. dollars).
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @returns {number|string|false} - The price in major unit or false.
 */
function minorToMajorUnit(priceInMinorUnit) {
  if (!validateNumber(priceInMinorUnit)) return false
  return (priceInMinorUnit / 100).toFixed(2)
}

/**
 * Simple formatter to convert price to string.
 * TODO: remove in favour of renderPrice below
 *
 * @param {number|string} priceInMinorUnit - The price in minor unit.
 * @param {string} [currencyUnit='$'] - The currency symbol.
 * @returns {string} - Formatted price string.
 */
function formatPrice(priceInMinorUnit, currencyUnit = '$') {
  const priceInMajorUnit = minorToMajorUnit(priceInMinorUnit)
  if (!priceInMajorUnit) return ''
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })
  return formatter.format(priceInMajorUnit)
}

/**
 * @typedef {Object} FormattedMoneyObject
 * @property {function(): {amount: number, currencyCode: string}} original - Returns the original money amount in minor units and currency code.
 * @property {function(): string} currencyCode - Returns the currency code.
 * @property {function(): string} currencyName - Returns the currency name.
 * @property {function(): string} currencySymbol - Returns the currency symbol.
 * @property {function(): string} currencyNarrowSymbol - Returns the narrow currency symbol.
 * @property {function(): string} amount - Returns the localized amount without currency symbols.
 * @property {function(): Intl.NumberFormatPart[]} parts - Returns the formatted parts.
 * @property {function(): string} localizedString - Returns the localized string with currency.
 * @property {function(): string} withoutTrailingZeros - Returns the localized string without trailing zeros.
 * @property {function(): string} withoutTrailingZerosAndCurrency - Returns the localized string without trailing zeros and currency.
 */

/**
 * Formats a money amount in minor units and currency code and returns a formatted money object with various formatting options.
 *
 * @param {number} amountInMinorUnits - The money amount in minor units (e.g., cents for USD).
 * @param {string} currencyCode - The currency code of the money amount.
 * @param {string} locale - The locale to use for formatting.
 * @returns {FormattedMoneyObject} The formatted money object with formatting options.
 */
export function renderPrice(
  amountInMinorUnits,
  currencyCode = Shopify.currency.active,
  locale = Shopify.locale,
) {
  if (
    !locale ||
    typeof amountInMinorUnits !== 'number' ||
    typeof currencyCode !== 'string'
  ) {
    return {
      original: () => '',
      standardized: () => '',
      currencyCode: () => '',
      localizedString: () => '',
      parts: () => [],
      withoutTrailingZeros: () => '',
      withoutTrailingZerosAndCurrency: () => '',
      currencyName: () => '',
      currencySymbol: () => '',
      currencyNarrowSymbol: () => '',
      amount: () => '',
    }
  }

  const amount = amountInMinorUnits / 10 ** getMinorUnits(currencyCode)

  const baseOptions = {
    style: 'currency',
    currency: currencyCode,
  }

  const withoutTrailingZerosOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }

  // Applies base options used for all formatters
  const defaultFormatter = createLazyFormatter(locale, baseOptions)

  // This should be the standard format we use
  const standardizedFormatter = createLazyFormatter(locale, {
    ...baseOptions,
    ...withoutTrailingZerosOptions, // without trailing zeros as standard
    currencyDisplay: 'narrowSymbol', // use narrow symbol as standard
  })

  const nameFormatter = createLazyFormatter(locale, {
    ...baseOptions,
    currencyDisplay: 'name',
  })

  const narrowSymbolFormatter = createLazyFormatter(locale, {
    ...baseOptions,
    currencyDisplay: 'narrowSymbol',
  })

  const withoutTrailingZerosFormatter = createLazyFormatter(locale, {
    ...baseOptions,
    ...withoutTrailingZerosOptions,
  })

  const withoutCurrencyFormatter = createLazyFormatter(locale)

  const withoutTrailingZerosOrCurrencyFormatter = createLazyFormatter(locale, {
    ...withoutTrailingZerosOptions,
  })

  const isPartCurrency = (part) => part.type === 'currency'

  return {
    original: () => ({ amount: amountInMinorUnits, currencyCode }),
    standardized: () =>
      Number.isNaN(amount) ? '' : standardizedFormatter().format(amount),
    currencyCode: () => currencyCode,
    localizedString: () =>
      Number.isNaN(amount) ? '' : defaultFormatter().format(amount),
    parts: () =>
      Number.isNaN(amount) ? [] : defaultFormatter().formatToParts(amount),
    withoutTrailingZeros: () =>
      Number.isNaN(amount)
        ? ''
        : withoutTrailingZerosFormatter().format(amount),
    withoutTrailingZerosAndCurrency: () =>
      Number.isNaN(amount)
        ? ''
        : withoutTrailingZerosOrCurrencyFormatter().format(amount),
    currencyName: () =>
      Number.isNaN(amount)
        ? ''
        : (nameFormatter().formatToParts(amount).find(isPartCurrency)?.value ??
          ''),
    currencySymbol: () =>
      Number.isNaN(amount)
        ? ''
        : (nameFormatter().formatToParts(amount).find(isPartCurrency)?.value ??
          ''),
    currencyNarrowSymbol: () =>
      Number.isNaN(amount)
        ? ''
        : (narrowSymbolFormatter().formatToParts(amount).find(isPartCurrency)
            ?.value ?? ''),
    amount: () =>
      Number.isNaN(amount)
        ? ''
        : withoutCurrencyFormatter()
            .formatToParts(amount)
            .filter((part) =>
              ['decimal', 'fraction', 'group', 'integer', 'literal'].includes(
                part.type,
              ),
            )
            .map((part) => part.value)
            .join(''),
  }
}

/**
 * Creates a lazy formatter function.
 *
 * @param {string} locale - The locale to use for formatting.
 * @param {Intl.NumberFormatOptions} [options] - The formatting options.
 * @returns {function(): Intl.NumberFormat} The lazy formatter function.
 */
function createLazyFormatter(locale, options) {
  let memoized
  return () => {
    if (!memoized) {
      memoized = new Intl.NumberFormat(locale, options)
    }
    return memoized
  }
}

/**
 * Gets the number of minor units for a given currency code based on ISO 4217 published on 2024-01-01.
 * https://www.six-group.com/dam/download/financial-information/data-center/iso-currrency/lists/list-one.xml
 * Note that this data has been analyzed and adjusted to account for de facto usage when shopping.
 * Adjustments are noted in the comments.
 *
 * @param {string} currencyCode - The currency code.
 * @returns {number} The number of minor units.
 */
function getMinorUnits(currencyCode) {
  switch (currencyCode) {
    case 'XOF': // VARIOUS - CFA Franc BCEAO
    case 'BIF': // BURUNDI - Burundi Franc
    case 'XAF': // VARIOUS - CFA Franc BEAC
    case 'CLP': // CHILE - Chilean Peso
    case 'KMF': // COMOROS (THE) - Comorian Franc
    case 'DJF': // DJIBOUTI - Djibouti Franc
    case 'XPF': // VARIOUS - CFP Franc
    case 'GNF': // GUINEA - Guinean Franc
    case 'ISK': // ICELAND - Iceland Krona
    case 'JPY': // JAPAN - Yen
    case 'KRW': // KOREA (THE REPUBLIC OF) - Won
    case 'PYG': // PARAGUAY - Guarani
    case 'RWF': // RWANDA - Rwanda Franc
    case 'UGX': // UGANDA - Uganda Shilling
    case 'VUV': // VANUATU - Vatu
    case 'VND': // VIET NAM - Dong
    /* The following have 2 minor units de jure (ISO 4217),
     * but only use 0 in practice for e-commerce. These values
     * should be ratified case-by-case as support is implemented
     * on the storefront */
    case 'MXN': // MEXICO - Mexican Peso
    case 'IDR': // INDONESIA - Rupiah
    case 'COP': // COLOMBIA - Colombian Peso
    case 'IRR': // IRAN (ISLAMIC REPUBLIC OF) - Iranian Rial
    case 'LAK': // LAO PEOPLE’S DEMOCRATIC REPUBLIC (THE) - Lao Kip
    case 'UZS': // UZBEKISTAN - Uzbekistan Sum
    case 'KHR': // CAMBODIA - Riel
    case 'MGA': // MADAGASCAR - Malagasy Ariary
    case 'MNT': // MONGOLIA - Tugrik
    case 'SLE': // SIERRA LEONE - Leone
      return 0
    case 'BHD': // BAHRAIN - Bahraini Dinar
    case 'IQD': // IRAQ - Iraqi Dinar
    case 'JOD': // JORDAN - Jordanian Dinar
    case 'KWD': // KUWAIT - Kuwaiti Dinar
    case 'LYD': // LIBYA - Libyan Dinar
    case 'OMR': // OMAN - Rial Omani
    case 'TND': // TUNISIA - Tunisian Dinar
      return 3
    case 'UYW': // URUGUAY - Unidad Previsional
      return 4
    default:
      return 2
  }
}

/**
 * Converts string in snake case to camel case.
 *
 * @param {string} string - String to convert.
 * @returns {string} - Converted string in camel case.
 */
function snakeToCamel(string) {
  return string.replace(/(_\w)/g, (matches) => matches[1].toUpperCase())
}

/**
 * Simple debounce function that prevents excessive function calls.
 *
 * @param {Function} func - Function to debounce.
 * @param {number} delay - Interval within which call cannot be re-executed.
 * @returns {Function} - Debounced function.
 */
function debounce(func, delay) {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

/**
 * Formats a number representing cents into a money string based on the provided format.
 *
 * @function
 * @param {(string | number)} centsInput - The amount of money in cents. If a string is provided, it will attempt to clean it.
 * @param {string} [formatInput] - The format to output the money string in. If not provided, it will use the global money format.
 * @returns {string} - The formatted money string.
 * @example
 * // Returns "$19.99"
 * formatMoney(1999, "{{amount}}");
 * @example
 * // Returns "19.99"
 * formatMoney(1999, "{{amount_no_decimals}}");
 */
export function formatMoney(centsInput, formatInput) {
  let cents = centsInput
  if (typeof cents === 'string') {
    cents = cents.replace('.', '')
  }

  const defaultTo = (value, defaultValue) =>
    value === null || value === undefined ? defaultValue : value

  let value = ''
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
  const formatString = formatInput || window.__NOVA_GLOBAL__.moneyFormat

  function formatWithDelimiters(
    number,
    precisionInput,
    thousandsInput,
    decimalInput,
  ) {
    const precision = defaultTo(precisionInput, 2)
    const thousands = defaultTo(thousandsInput, ',')
    const decimal = defaultTo(decimalInput, '.')

    if (Number.isNaN(number) || number === null) {
      return 0
    }

    const formattedNumber = (number / 100.0).toFixed(precision)

    const parts = formattedNumber.split('.')
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`,
    )
    const centsAmount = decimal + (parts[1] || '')

    return dollarsAmount + centsAmount
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2)
      break
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0)
      break
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',')
      break
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',')
      break
  }

  // return formatString.replace(placeholderRegex, value).replace('.00', '');
  return formatString.replace(placeholderRegex, value)
}

/**
 * Checks if a given string represents a valid stringified array.
 *
 * @param {string} string - The string to check.
 * @returns {boolean} - Returns true if the string is a valid stringified array, otherwise false.
 */
function isStringifiedArray(string) {
  if (typeof string !== 'string') return false
  if (string.startsWith('[') && string.endsWith(']')) {
    try {
      const potentialArray = JSON.parse(string)
      return Array.isArray(potentialArray)
    } catch (error) {
      return false
    }
  }
  return false
}

/**
 * Adds one or more product variants to the Shopify cart.
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to add to the cart.
 * Each object should have an `id` property (variant ID) and a `quantity` property.
 * @param {boolean} [openCart=true] - Whether to trigger the cart to open after adding items.
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * addToCart({ id: '1234567890', quantity: 1 });
 * addToCart([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
const addToCart = async (items, openCart = true) => {
  let itemsArray = items
  if (!Array.isArray(items)) {
    itemsArray = [items]
  }
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: itemsArray }),
    })
    if (!response.ok) {
      throw new Error('Network response was not ok')
    }
    if (openCart) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:reload', {
          bubbles: true,
          composed: true,
          detail: { open: true },
        }),
      )
    }
  } catch (error) {
    console.error(
      'There was a problem with the fetch operation:',
      error.message,
    )
    throw error
  }
}

/**
 * Update the cart's line item quantities
 *
 * @async
 * @function
 * @param {Array|Object} items - An array of objects or a single object representing the items to update
 * Each object should have an `id` property (variant ID or line item key) and a `quantity` property.
 * @param {boolean} [openCart=true] Whether to trigger the cart to open after updating.
 * @throws Will throw an error if the fetch operation encounters a problem.
 * @example
 * updateCartQuantity({ id: '1234567890', quantity: 1 });
 * updateCartQuantity([{ id: '1234567890', quantity: 1 }, { id: '0987654321', quantity: 2 }]);
 */
async function updateCartQuantity(items, openCart = true) {
  let itemsArray = items
  if (!Array.isArray(items)) {
    itemsArray = [items]
  }

  try {
    const updates = {}
    for (const item of itemsArray) {
      updates[item.id] = item.quantity
    }

    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ updates }),
    })

    if (!response.ok) {
      throw new Error('Error updating quantity in cart')
    }

    if (openCart) {
      document.dispatchEvent(
        new CustomEvent('nova:cart:open', { bubbles: true, composed: true }),
      )
    }
    // document.dispatchEvent(
    //   new CustomEvent('nova:cart:refresh', {
    //     bubbles: true,
    //     composed: true,
    //   }),
    // )

    const responseData = await response.json()
    return responseData
  } catch (error) {
    console.error(
      'There was a problem with the fetch operation:',
      error.message,
    )
  }
}

/**
 * Interpolates a template string with provided replacements.
 *
 * @param {string} template - The string template with placeholders.
 * @param {Object} replacements - An object containing key-value pairs for placeholders and their replacements.
 * @returns {string} The interpolated string.
 *
 * @example
 *   const messageTemplate = 'Hello, {name}! You have {count} messages.';
 *   const values = {
 *     name: 'Alice',
 *     count: 5
 *   }
 *   const message = interpolate(messageTemplate, values);
 *   console.log(message); // "Hello, Alice! You have 5 messages."
 */
function interpolate(template, replacements) {
  return template.replace(/\{(\w+)\}/g, (_, placeholder) => {
    return replacements[placeholder] || _
  })
}

/**
 * Finds and returns a slotted child within a Web Component
 *
 * @param {DOMElement} shadowRoot
 * @param {String} name
 * @param {Boolean} firstOnly
 * @returns DOMElement | Empty
 */
function getChildSlot(shadowRoot = null, name = '', firstOnly = false) {
  const children =
    shadowRoot
      ?.querySelector('slot')
      ?.assignedElements({ flatten: true })
      .filter((node) => {
        return node.matches(name)
      }) || []
  return firstOnly ? (children || [])[0] : children
}

/**
 * A shorthand for querySelector
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
function one(selector, parent) {
  const parentElement = parent || document
  return parentElement.querySelector(selector)
}

/**
 * A shorthand for querySelectorAll
 *
 * @param {String} selector
 * @param {DomElement} parent
 * @returns DOMElement | Empty
 */
function all(selector, parent) {
  const parentElement = parent || document
  const selection = parentElement.querySelectorAll(selector)
  return Array.prototype.slice.call(selection)
}

/**
 * Toggles an array of classes on a DOMElement
 *
 * @param {DOMElement} element
 * @param {Array} classes
 */
function toggle(element, classes = []) {
  for (const _class of classes) {
    element.classList.toggle(_class)
  }
}

/**
 * Not lovely & not pure; attempts to pause all currently
 * playing media (ie. videos)
 */
const pauseAllMedia = () => {
  const $youTubeElements = document.querySelectorAll('.js-youtube')
  for (const $video of $youTubeElements) {
    $video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*',
    )
  }
  const $vimeoElements = document.querySelectorAll('.js-vimeo')
  for (const $video of $vimeoElements) {
    $video.contentWindow.postMessage('{"method":"pause"}', '*')
  }
  const $videoElements = document.querySelectorAll('video')
  for (const $video of $videoElements) {
    video.pause()
  }
  const $productModelElements = document.querySelectorAll('product-model')
  for (const $model of $productModelElements) {
    $model.modelViewerUI?.pause()
  }
}

/**
 * A classic throttle helper
 *
 * @param {Function} callback
 * @param {Number} limit
 * @returns
 */
const throttle = (callback, limit) => {
  let waiting = false
  return function (...args) {
    if (!waiting) {
      callback.apply(this, args)
      waiting = true
      setTimeout(() => {
        waiting = false
      }, limit)
    }
  }
}

/**
 * Serializes form
 *
 * @param {Form} form
 * @returns String
 */
const serializeForm = (form) => {
  const obj = {}
  const formData = new FormData(form)

  for (const key of formData.keys()) {
    // ensures that the line item properties are added as an object, instead of as an array
    const regex = /(?:^(properties\[))(.*?)(?:\]$)/

    if (regex.test(key)) {
      obj.properties = obj.properties || {}
      obj.properties[regex.exec(key)[2]] = formData.get(key)
    } else {
      obj[key] = formData.get(key)
    }
  }

  return JSON.stringify(obj)
}

/**
 * Returns a generic fetch config object (XHR)
 *
 * @param {String} type
 * @param {String} method
 * @returns Object
 */
const fetchConfig = (type = 'json', method = 'POST') => {
  return {
    method,
    headers: {
      'Content-Type': 'application/json',
      Accept: `application/${type}`,
    },
  }
}

/**
 * Checks to see if the device supports touch gestures.
 */
const isTouch = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints
}

/**
 * Clones a complex Javascript object. This is a
 * thorough way to ensure that the object is fully
 * cloned with no references kept in place.
 *
 * @param {Object} obj
 */
const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Removes whitespace within a string and makes all
 * characters lowercase.
 *
 * @param {String} str
 */
const handleize = (str) => str.replace(/[ /_]/g, '-').toLowerCase()

/**
 * Decodes a string that has been encoded through the 'url_encode' Shopify filter
 * @param {*} str
 */
const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ')

/**
 * Returns an iFrame src URL value for either YouTube or Vimeo hosted videos
 *
 * @param {string} host 'youtube' or 'vimeo'
 * @param {string} id the external video ID
 * @param {boolean} autoplay whether the video should autoplay
 * @param {boolean} controls whether the video should show controls
 * @param {boolean} loop whether the video should loop
 */
const externalVideoEmbedSrc = (
  host,
  id,
  autoplay = false,
  controls = false,
  loop = false,
  sound = false,
) => {
  let src = ''
  const queryParam = (param, value) => {
    return `${param}=${Number(value)}`
  }
  const autoplayParam = queryParam('autoplay', autoplay)
  const controlsParam = queryParam('controls', controls)
  const loopParam = queryParam('loop', loop)
  const queryParams = `${autoplayParam}&${controlsParam}&${loopParam}`
  let muted

  switch (host) {
    case 'youtube':
      muted = sound ? '' : 'mute=1&'
      src = `https://www.youtube.com/embed/${id}?${queryParams}&enablejsapi=1&modestbranding=1&${muted}cc_load_policy=0&disablekb=0&fs=1&iv_load_policy=1&playsinline=1&rel=1&widgetid=1`
      break
    case 'vimeo':
      muted = sound ? '' : 'muted=1&'
      src = `https://player.vimeo.com/video/${id}?${queryParams}&${muted}byline=0&playsinline=1&title=0`
      break
    default:
      console.error('Invalid host value (must be "youtube" or "vimeo")')
  }
  return src
}

/**
 * Gets the previous sibling of an element that matches a given selector
 *
 * @param {HTMLElement} element the element to start from
 * @param {string} selector the selector to match
 */
const getPreviousSibling = (element, selector) => {
  let sibling = element.previousElementSibling

  if (!selector) return null

  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling
    }
    sibling = sibling.previousElementSibling
  }
}

const t = (namespace, fallback) => {
  return window.langMessages[namespace]
    ? window.langMessages[namespace]
    : fallback
}

/**
 * Determines the current viewport category based on the window's inner width.
 *
 * @function
 * @returns {string} - The current viewport category, which can be 'mobile', 'tablet', or 'desktop'.
 * @example
 * const viewport = getViewport();
 * console.log(viewport); // Outputs: 'mobile', 'tablet', or 'desktop'
 */
function getViewport() {
  const width = window.innerWidth
  let viewport = 'large-desktop'
  if (width <= 400) {
    viewport = 'small-mobile'
  } else if (width > 400 && width <= 480) {
    viewport = 'mobile'
  } else if (width > 480 && width <= 990) {
    viewport = 'tablet'
  } else if (width > 990 && width <= 1440) {
    viewport = 'desktop'
  }
  return viewport
}

/**
 * Determines if the current device supports touch input.
 *
 * @function
 * @returns {boolean} - True if the device supports touch input, otherwise false.
 * @example
 * const isTouchDevice = checkIsTouchDevice();
 * console.log(isTouchDevice); // Outputs: true or false
 */
function checkIsTouchDevice() {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  )
}

export {
  validateNumber,
  minorToMajorUnit,
  formatPrice,
  snakeToCamel,
  debounce,
  addToCart,
  updateCartQuantity,
  interpolate,
  getChildSlot,
  one,
  all,
  toggle,
  pauseAllMedia,
  throttle,
  serializeForm,
  fetchConfig,
  isTouch,
  deepClone,
  handleize,
  decode,
  externalVideoEmbedSrc,
  getPreviousSibling,
  t,
  getViewport,
  checkIsTouchDevice,
  isStringifiedArray,
}
